import axios from "axios";

// 创建axios实例
const request = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "/api", // 使用环境变量，默认值为/api
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// 服务器端和客户端环境判断
const isClient = typeof window !== "undefined";

// 请求拦截器 - 完全兼容Node环境
request.interceptors.request.use(
  async (config) => {
    // 打印请求参数
    console.log('========================================');
    console.log('API Request:');
    console.log('URL:', config.url);
    console.log('Method:', config.method?.toUpperCase());
    if (config.params) {
      console.log('Query Params:', config.params);
    }
    if (config.data) {
      console.log('Request Body:', config.data);
    }
    console.log('========================================');

    // 添加认证token - 只在客户端环境执行
    if (isClient) {
      try {
        // 延迟导入以避免SSR问题
        const { useUserStore } = await import("@/stores/user");
        const store = useUserStore();
        if (store.isLoggedIn && store.userInfo?.id) {
          config.headers.Authorization = `Bearer ${store.userInfo.id}`;
        }
      } catch (error) {
        // 在客户端环境安全地处理错误
        if (isClient) {
          console.error("Error loading user store:", error);
        }
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 完全兼容Node环境
request.interceptors.response.use(
  (response) => {
    const { data } = response;
    
    // 打印响应数据
    console.log('========================================');
    console.log('API Response:');
    console.log('URL:', response.config?.url);
    console.log('Status:', response.status);
    console.log('Response Data:', data);
    console.log('========================================');

    // 如果返回的数据有特定的格式，可以在这里统一处理
    if (data.code && data.code !== 200) {
      const errorMessage = data.message || "请求失败";
      // 使用原生alert替代console.error - 只在客户端环境
      if (isClient) {
        alert(errorMessage);
      } else {
        // 服务端环境记录错误
        console.error("API Error:", errorMessage);
      }
      return Promise.reject(new Error(errorMessage));
    }

    return data;
  },
  async (error) => {
    // 处理HTTP错误 - 完全兼容Node环境
    if (error.response) {
      const { status, data } = error.response;
      let errorMessage = "";

      switch (status) {
        case 401:
          errorMessage = "未授权，请重新登录";
          // 清除用户信息 - 只在客户端环境执行
          if (isClient) {
            try {
              const { useUserStore } = await import("@/stores/user");
              const userStore = useUserStore();
              userStore.clearUserInfo();
              // 只对需要认证的页面进行重定向
              // 公开页面（如首页、分类页）不重定向，保持用户体验
              const currentPath = window.location.pathname;
              const publicPaths = [
                "/",
                "/category",
                "/news",
                "/exhibition",
                "/industry",
                "/history",
                "/about",
              ];

              // 检查当前页面是否为需要认证的页面
              if (!publicPaths.includes(currentPath)) {
                // 安全的页面跳转
                window.location.href = "/";
              }
            } catch (storeError) {
              console.error("Error in auth handling:", storeError);
            }
          } else {
            console.error("Unauthorized access in server-side rendering");
          }
          break;
        case 403:
          errorMessage = "权限不足";
          break;
        case 404:
          errorMessage = "请求的资源不存在";
          break;
        case 500:
          errorMessage = "服务器内部错误";
          break;
        default:
          errorMessage = data?.message || "请求失败";
      }

      // 显示错误信息 - 根据环境选择合适的方式
      if (isClient) {
        alert(errorMessage);
      } else {
        console.error(`HTTP Error ${status}:`, errorMessage);
      }
    } else if (error.request) {
      const errorMessage = "网络错误，请检查网络连接";
      if (isClient) {
        alert(errorMessage);
      } else {
        console.error("Network error:", errorMessage);
      }
    } else {
      const errorMessage = "请求配置错误";
      if (isClient) {
        alert(errorMessage);
      } else {
        console.error("Request configuration error:", errorMessage);
      }
    }

    return Promise.reject(error);
  }
);

export default request;
